import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ComponentRef, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { ActivatedRoute, NavigationEnd, Router, RouterEvent } from '@angular/router';
import { CompanyModel } from '@app/data-layer/models/companies';
import { BillItemPendingModel } from '@app/data-layer/models/payments';
import { BillsRepository, CompaniesRepository } from '@app/data-layer/repositories';
import { AppLayoutService } from '@app/modules/app-layout/app-layout.service';
import { IHeaderItem } from '@app/modules/app-layout/interfaces';
import { CbpAssignedNumberRequestService } from '@services/cbp-assigned-number-request.service';
import { CompaniesService } from '@services/companies.service';
import { Observable, ReplaySubject, of } from 'rxjs';
import { finalize, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { EditCompanyComponent } from '../../components';
import { enumCompanyPlacement } from '@enum/enumCompanyPlacement';
import { AppService } from '@app/app.service';
import { enumCompanyPaymentType } from '@app/data-layer/enums/companies';
import { OrganizationModel } from '@app/data-layer/models/organizations';
import { OrganizationsRepository } from '@app/data-layer/repositories/organizations.repository';
import { IorMatchGateService } from '@services/ior-match-gate.service';

@Component({
  selector: 'company-cbp-assigned-number-request-page',
  templateUrl: './company-cbp-assigned-number-request-page.component.html',
  styleUrls: ['./company-cbp-assigned-number-request-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CompanyCbpAssignedNumberRequestPageComponent implements OnInit, OnDestroy {
  public formGroup: UntypedFormGroup;
  public paymentBreakdownNotes: string[] = ['LABELS.ASSIGNED_CBP_NUMBER_POST_PAYMENT_PROCESSING'];
  public isLoadingBills: boolean = true;
  public pendingBills: BillItemPendingModel[] = [];
  public company: CompanyModel;
  public isShowSendButton: boolean = false;
  public isSending: boolean = false;

  private organization: OrganizationModel;
  private destroy$: ReplaySubject<void> = new ReplaySubject<void>();

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private cdr: ChangeDetectorRef,
    private appService: AppService,
    private billsRepository: BillsRepository,
    private appLayoutService: AppLayoutService,
    private companiesService: CompaniesService,
    private companiesRepository: CompaniesRepository,
    private cbpAssignedNumberRequestService: CbpAssignedNumberRequestService,
    private organizationsRepository: OrganizationsRepository,
    private iorMatchGate: IorMatchGateService,
  ) {}

  ngOnInit() {
    this.initFormGroup();

    const companyId = this.route.snapshot.params.id;

    this.formGroup = this.cbpAssignedNumberRequestService.getFormGroup();

    this.companiesService
      .getCompanyByIdFromServer(companyId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((company) => {
        this.company = company;
        this.companiesService.setDefaultCompany(company);
        this.populateFormGroupBusinessDetails();
        this.syncCbpAssignedNumberRegistrationDetails().pipe(takeUntil(this.destroy$)).subscribe();

        this.organizationsRepository
          .getOrganizationById(this.company.organizationId)
          .pipe(takeUntil(this.destroy$))
          .subscribe((org) => {
            this.organization = org;
            this.updateButtonState();
          });
      });

    const title: IHeaderItem[] = [];
    title.push(
      {
        key: 'LABELS.IMPORTERS_OF_RECORD',
        link: '/companies/account-settings/company-list',
      },
      { key: 'MENU.CBP_ASSIGNED_NUMBER_REQUEST', link: null },
    );
    this.appLayoutService.contentHeader$.next({ title, mobileTitle: title });

    this.router.events.pipe(takeUntil(this.destroy$)).subscribe((event: RouterEvent) => {
      if (event instanceof NavigationEnd) {
        this.updateButtonState();
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
  }

  public navigateBackToCompany(isSent: boolean = false): void {
    if (isSent) {
      this.router.navigate(['/companies', 'account-settings', 'company-view', this.company?.id], {
        queryParams: {
          cbpRequest: this.company?.id,
          requiresValidation: this.company?.id,
          isEditViewOpen: true,
        },
      });
    } else {
      this.router.navigate(['/companies', 'account-settings', 'company-view', this.company?.id], { queryParams: { isEditViewOpen: true } });
    }
  }

  public continue() {
    const user = this.companiesService.getUser();
    if (!user.phoneNumber) {
      this.fillCompany();
      return;
    }

    const currentRouteUrl = location.pathname;
    this.syncCbpAssignedNumberRegistrationDetails()
      .pipe(
        switchMap((pendingBills) => {
          if (currentRouteUrl.includes('application')) {
            return this.iorMatchGate.confirmIorMatchOrPass().pipe(
              take(1),
              switchMap(() => this.updateCompany(this.company)),
            );
          } else {
            return of(pendingBills);
          }
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((_) => {
        if (currentRouteUrl.includes('start')) {
          this.start();
        } else if (currentRouteUrl.includes('application')) {
          this.navigateAndPay();
        }
      });
  }

  public send() {
    const currentRouteUrl = location.pathname;

    if (this.isSending) return;

    this.isSending = true;

    this.billsRepository
      .create(this.company.id, {
        referenceNumbers: [],
        coupon: null,
        paymentProvider: null,
        paymentCodeId: null,
      })
      .pipe(
        switchMap(() => this.syncCbpAssignedNumberRegistrationDetails()),
        switchMap((pendingBills) => {
          if (currentRouteUrl.includes('application')) {
            return this.iorMatchGate.confirmIorMatchOrPass().pipe(
              take(1),
              switchMap(() => this.updateCompany(this.company)),
            );
          } else {
            return of(pendingBills);
          }
        }),
        takeUntil(this.destroy$),
        finalize(() => (this.isSending = false)),
      )
      .subscribe(
        (_) => {
          this.navigateBackToCompany(true);
        },
        (error) => {
          this.isSending = false;
        },
      );
  }

  public updateCompany(company: CompanyModel): Observable<CompanyModel> {
    return this.companiesRepository
      .update(company?.id, {
        ...company,
        identifier: null,
        identifierQualifier: null,
        address: this.formGroup.get('address').value,
        businessType: this.formGroup.get('businessType').value,
        // #13233 — carry the State of Incorporation selection to the company update (null when not applicable)
        stateOfIncorporation: this.formGroup.get('stateOfIncorporation').value || null,
        businessName: this.formGroup.get('businessName').value,
        dateOfBirth: this.formGroup.get('dateOfBirth').value,
        isImporterOfRecord: this.formGroup.get('isImporterOfRecord')?.value,
        importerOfRecordContactInformation: (this.formGroup.get('importerOfRecordContactInformation') as UntypedFormGroup)?.getRawValue(),
      })
      .pipe(switchMap(() => this.companiesService.getCompanyAsync(true, false, company?.id)));
  }

  public fillCompany(): void {
    const formGroup: UntypedFormGroup = this.companiesService.createCompanyForm(enumCompanyPlacement.CBP_ASSIGNED_NUMBER_REQUEST);
    const componentSidebarRef: ComponentRef<EditCompanyComponent> = this.appService.openSidebar(EditCompanyComponent, {
      sidebarHeaderText: 'LABELS.COMPLETE_REGISTRATION',
      tooltipText: 'LABELS.COMPLETE_REGISTRATION_HINT',
    });

    componentSidebarRef.instance.formGroup = formGroup;

    //Sync cbp form with sidebar form
    componentSidebarRef.instance.formGroup.get('address').patchValue(this.formGroup.get('address').value);
    componentSidebarRef.instance.formGroup.get('businessType').patchValue(this.formGroup.get('businessType').value);
    componentSidebarRef.instance.formGroup.get('businessName').patchValue(this.formGroup.get('businessName').value);
    componentSidebarRef.instance.formGroup.get('stateOfIncorporation').patchValue(this.formGroup.get('stateOfIncorporation').value);

    componentSidebarRef.instance.placement = enumCompanyPlacement.CBP_ASSIGNED_NUMBER_REQUEST;
    componentSidebarRef.instance.updateCompanySource$.pipe(takeUntil(this.destroy$)).subscribe((companyForm: UntypedFormGroup) => {
      this.appService.closeSidebar();
      const currentRouteUrl = location.pathname;
      if (currentRouteUrl.includes('start')) {
        //Sync sidebar form with cbp form
        this.formGroup.get('address').patchValue(companyForm.get('address').value);
        this.formGroup.get('businessName').patchValue(companyForm.get('businessName').value);

        this.start();
      } else if (currentRouteUrl.includes('application')) {
        this.navigateAndPay();
      }
      this.cdr.detectChanges();
    });
  }

  public get isContinueDisabled(): boolean {
    const currentRouteUrl = location.pathname;
    if (!currentRouteUrl.includes('application')) {
      return this.isLoadingBills;
    }

    return this.formGroup.invalid || this.isAnyBusinessTypeRelatedDocsMissing || this.isLoadingBills;
  }

  public get isAnyBusinessTypeRelatedDocsMissing(): boolean {
    return this.cbpAssignedNumberRequestService.getIsAnyBusinessTypeRelatedDocsMissing();
  }

  private start() {
    this.router.navigate(['/companies', 'cbp-assigned-number-request', this.company?.id, 'application'], {
      queryParams: {
        businessType: this.formGroup.get('businessType').value,
        identifierQualifier: this.formGroup.get('identifierQualifier').value,
      },
    });
  }

  private navigateAndPay(): void {
    this.router.navigate(['payment'], {
      queryParams: {
        cbpRequest: this.company?.id,
        requiresValidation: this.company?.id,
        referenceNumbers: ['company'],
        returnUrl: `/companies/account-settings/company-view/${this.company?.id}`,
      },
    });
  }

  private syncCbpAssignedNumberRegistrationDetails(): Observable<BillItemPendingModel[]> {
    return this.companiesRepository
      .updateCbpAssignedNumberRegistrationDetails(this.company?.id, {
        identifier: this.formGroup.get('identifier').value || null,
        identifierQualifier: this.formGroup.get('identifierQualifier').value || null,
        businessType: this.formGroup.get('businessType').value,
      })
      .pipe(
        switchMap((_) => this.getPendingBills()),
        tap((pendingBills) => {
          this.pendingBills = pendingBills;
          this.isLoadingBills = false;
          this.cdr.markForCheck();
        }),
        takeUntil(this.destroy$),
        finalize(() => (this.isLoadingBills = false)),
      );
  }

  private getPendingBills(): Observable<BillItemPendingModel[]> {
    return this.billsRepository.pendingBills(
      this.company.id,
      {
        referenceNumbers: [],
        paymentCodeId: null,
        paymentProvider: null,
      },
      true,
    );
  }

  private initFormGroup() {
    this.cbpAssignedNumberRequestService.initializeFormGroup();
  }

  private populateFormGroupBusinessDetails() {
    this.formGroup.get('businessName').patchValue(this.company?.businessName || '');
    this.formGroup.get('address').patchValue(this.company?.address);
    // #13233 — State of Incorporation stored on the company
    this.formGroup.get('stateOfIncorporation').patchValue(this.company?.stateOfIncorporation || null, { emitEvent: false });
    this.formGroup.get('isImporterOfRecord')?.patchValue(this.company?.isImporterOfRecord ?? false, { emitEvent: false });
    if (this.company?.importerOfRecordContactInformation) {
      this.formGroup
        .get('importerOfRecordContactInformation')
        ?.patchValue(this.company.importerOfRecordContactInformation, { emitEvent: false });
    }
  }

  private updateButtonState() {
    const paymentType = this.company?.cbpAssignedNumberPaymentType ?? this.organization?.cbpAssignedNumberPaymentType;
    const currentRouteUrl = location.pathname;

    this.isShowSendButton = paymentType === enumCompanyPaymentType.MONTHLY && currentRouteUrl.includes('application');
  }
}
